import React from 'react'
import { useLocation } from 'react-router-dom'
import {
  PixelBreadcrumbs,
  PixelFactoryContext,
  PixelFlexBox,
  PixelHeaderPanel,
  PixelPageContainer,
  PixelText,
  PixelTopBar,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from 'pixel-base'
import queryString from 'query-string'

import { getDarazStatementDetails } from 'utils/api-routes.util'
import { IPixelFactory } from '../../../../../decl.d'

const breadcrumbs = [
  {
    name: 'Daraz Statements',
    active: false,
    link: '/payments',
  },
  {
    name: 'Details',
    active: true,
  },
]

interface Payload {
  statement_number: string
}

interface Data {
  id: string | null
  statement_number: string | null
  statement_name: string | null
  total_payout: number | null
  outstanding_amount_prev_statement: number | null
  total_order_amount: number | null
  delivered_orders: number | null
  shipping_fee_paid_by_buyer: number | null
  product_price_paid_by_buyer: number | null
  transaction_fee: number | null
  payment_fee: number | null
  commission_fee: number | null
  shipping_fee: number | null
  shipping_fee_subsidy: number | null
  delivered_orders_marked_failed: number | null
  delivered_withholding: number | null
  VAT: number | null
  delivered_subtotal: number | null
  returned_orders: number | null
  transaction_fees_refunded: number | null
  payment_fee_refunded: number | null
  commission_fee_refunded: number | null
  returned_orders_marked_delivered: number | null
  returned_withholding: number | null
  returned_VAT: number | null
  returned_subtotal: number | null
  logistics_fulfillment_services: number | null
  handling_fee: number | null
  advertising_marketing_services: number | null
  financial_services: number | null
  inventory_management: number | null
  services_VAT: number | null
  services_subtotal: number | null
}

const DarazStatmentDetails: React.FC = () => {
  const { showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const location = useLocation()
  const p = queryString.parse(location.search)
  const statement_number = p.statement_number ? p.statement_number : ''
  const [payload] = React.useState<Payload>({
    statement_number: statement_number,
  })
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [data, setData] = React.useState<Data | null>(null)

  React.useEffect(() => {
    payload.statement_number && getAllDarazStatementDetails()
  }, [payload])

  const getAllDarazStatementDetails = () => {
    showPageLoader()
    setIsLoading(true)
    getDarazStatementDetails(payload).subscribe((res: any) => {
      setData(res.response.data)
      hidePageLoader()
      setIsLoading(false)
    })
  }

  return (
    <>
      <PixelTopBar>
        <PixelBreadcrumbs data={breadcrumbs} />
      </PixelTopBar>
      <PixelPageContainer>
        <Section>
          <SectionHeader>
            <PixelFlexBox>
              <SectionTitle>Statement Details</SectionTitle>
            </PixelFlexBox>
          </SectionHeader>
          {isLoading ? (
            <SectionContent>
              <PixelText style={{ textAlign: 'center' }} textSize="25px">
                Processing...
              </PixelText>
            </SectionContent>
          ) : data ? (
            <SectionContent style={{ paddingBottom: '30px' }}>
              <PixelHeaderPanel backgroundColor="#e9ecef78">
                <PixelFlexBox padding="20px">
                  <PixelFlexBox flexDirection="column">
                    {data?.total_payout && <PixelText>Total Payout</PixelText>}
                    <PixelText style={{ marginBottom: '30px' }}>
                      Statement Date:
                    </PixelText>
                    {data?.total_payout && <PixelText>Total Balance</PixelText>}
                    {data?.outstanding_amount_prev_statement && (
                      <PixelText>
                        Outstanding amount from previous statement
                      </PixelText>
                    )}
                    {data?.delivered_subtotal && (
                      <PixelText>Total Delivered Orders</PixelText>
                    )}
                    {data?.returned_subtotal && (
                      <PixelText>Total Returned Orders</PixelText>
                    )}
                    {data?.services_subtotal && (
                      <PixelText>Total Services</PixelText>
                    )}
                  </PixelFlexBox>
                  <PixelFlexBox
                    flexDirection="column"
                    margin="0 0 0 auto"
                    style={{ textAlign: 'end' }}
                  >
                    {data?.total_payout && (
                      <PixelText>{data?.total_payout} PKR</PixelText>
                    )}
                    <PixelText style={{ marginBottom: '30px' }}>
                      {data?.statement_name
                        ? data?.statement_name
                        : 'Not found'}
                    </PixelText>
                    {data?.total_payout && data?.total_payout}
                    {data?.outstanding_amount_prev_statement && (
                      <PixelText>
                        {data?.outstanding_amount_prev_statement} PKR
                      </PixelText>
                    )}
                    {data?.delivered_subtotal && (
                      <PixelText>{data?.delivered_subtotal} PKR</PixelText>
                    )}
                    {data?.returned_subtotal && (
                      <PixelText>{data?.returned_subtotal} PKR</PixelText>
                    )}
                    {data?.services_subtotal && (
                      <PixelText>{data?.services_subtotal} PKR</PixelText>
                    )}
                  </PixelFlexBox>
                </PixelFlexBox>
              </PixelHeaderPanel>

              {data?.delivered_orders && (
                <PixelText
                  textSize="20px"
                  style={{
                    fontWeight: '500',
                    marginTop: '20px',
                    marginLeft: '10px',
                  }}
                >
                  Delivered Orders
                </PixelText>
              )}
              <PixelFlexBox padding="0 30px">
                <PixelFlexBox flexDirection="column">
                  {data?.delivered_orders && (
                    <PixelText>Delivered Orders</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 25px"
                  >
                    {data?.shipping_fee_paid_by_buyer && (
                      <PixelText>Shipping Fee Paid by Buyer</PixelText>
                    )}
                    {data?.product_price_paid_by_buyer && (
                      <PixelText>Product Price Paid by Buyer</PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.transaction_fee && (
                    <PixelText>Transaction Fees</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 25px"
                  >
                    {data?.payment_fee && <PixelText>Payment Fee</PixelText>}
                    {data?.commission_fee && (
                      <PixelText>Commission Fee</PixelText>
                    )}
                    {data?.shipping_fee && <PixelText>Shipping Fee</PixelText>}
                    {data?.shipping_fee_subsidy && (
                      <PixelText>Shipping Fee Subsidy by Daraz</PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.delivered_orders_marked_failed && (
                    <PixelText>
                      Delivered Orders Marked Delivery Failed
                    </PixelText>
                  )}
                  {data?.delivered_withholding && (
                    <PixelText>Withholding</PixelText>
                  )}
                  {data?.VAT && <PixelText>VAT</PixelText>}
                  {data?.delivered_subtotal && (
                    <PixelText
                      style={{
                        marginLeft: '500px',
                        marginTop: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      Subtotal
                    </PixelText>
                  )}
                </PixelFlexBox>
                <PixelFlexBox
                  style={{ textAlign: 'end' }}
                  flexDirection="column"
                >
                  {data?.delivered_orders && (
                    <PixelText>{data.delivered_orders} PKR</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 0"
                  >
                    {data?.shipping_fee_paid_by_buyer && (
                      <PixelText>
                        {data.shipping_fee_paid_by_buyer} PKR
                      </PixelText>
                    )}
                    {data?.product_price_paid_by_buyer && (
                      <PixelText>
                        {data.product_price_paid_by_buyer} PKR
                      </PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.transaction_fee && (
                    <PixelText>{data?.transaction_fee} PKR</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 0"
                  >
                    {data?.payment_fee && (
                      <PixelText>{data.payment_fee} PKR</PixelText>
                    )}
                    {data?.commission_fee && (
                      <PixelText>{data.commission_fee} PKR</PixelText>
                    )}
                    {data?.shipping_fee && (
                      <PixelText>{data.shipping_fee} PKR</PixelText>
                    )}
                    {data?.shipping_fee_subsidy && (
                      <PixelText>{data.shipping_fee_subsidy} PKR</PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.delivered_orders_marked_failed && (
                    <PixelText>
                      {data.delivered_orders_marked_failed} PKR
                    </PixelText>
                  )}
                  {data?.delivered_withholding && (
                    <PixelText>{data.delivered_withholding} PKR</PixelText>
                  )}
                  {data?.VAT && <PixelText>{data.VAT} PKR</PixelText>}
                  {data?.delivered_subtotal && (
                    <PixelText
                      style={{ marginTop: '30px', fontWeight: 'bold' }}
                    >
                      {data.delivered_subtotal} PKR
                    </PixelText>
                  )}
                </PixelFlexBox>
              </PixelFlexBox>

              {data?.returned_orders && (
                <PixelText
                  textSize="20px"
                  style={{
                    fontWeight: '500',
                    marginTop: '20px',
                    marginLeft: '10px',
                  }}
                >
                  Returned Orders
                </PixelText>
              )}
              <PixelFlexBox padding="0 30px">
                <PixelFlexBox flexDirection="column">
                  {data?.returned_orders && (
                    <PixelText>Returned Orders</PixelText>
                  )}
                  {data?.transaction_fees_refunded && (
                    <PixelText>Transaction Fees Refunded</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 25px"
                  >
                    {data?.payment_fee_refunded && (
                      <PixelText>Payment Fee Refunded</PixelText>
                    )}
                    {data?.commission_fee_refunded && (
                      <PixelText>Commission Fee Refunded</PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.returned_orders_marked_delivered && (
                    <PixelText>Returned Orders Marked Delivered</PixelText>
                  )}
                  {data?.returned_withholding && (
                    <PixelText>Withholding</PixelText>
                  )}
                  {data?.returned_VAT && <PixelText>VAT</PixelText>}
                  {data?.returned_subtotal && (
                    <PixelText
                      style={{
                        marginLeft: '500px',
                        marginTop: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      Subtotal
                    </PixelText>
                  )}
                </PixelFlexBox>
                <PixelFlexBox
                  style={{ textAlign: 'end' }}
                  flexDirection="column"
                >
                  {data?.returned_orders && (
                    <PixelText>{data.returned_orders} PKR</PixelText>
                  )}
                  {data?.transaction_fees_refunded && (
                    <PixelText>{data.transaction_fees_refunded} PKR</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 0"
                  >
                    {data?.payment_fee_refunded && (
                      <PixelText>{data.payment_fee_refunded} PKR</PixelText>
                    )}
                    {data?.commission_fee_refunded && (
                      <PixelText>{data.commission_fee_refunded} PKR</PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.returned_orders_marked_delivered && (
                    <PixelText>
                      {data.returned_orders_marked_delivered} PKR
                    </PixelText>
                  )}
                  {data?.returned_withholding && (
                    <PixelText>{data.returned_withholding} PKR</PixelText>
                  )}
                  {data?.returned_VAT && (
                    <PixelText>{data.returned_VAT} PKR</PixelText>
                  )}
                  {data?.returned_subtotal && (
                    <PixelText
                      style={{ marginTop: '30px', fontWeight: 'bold' }}
                    >
                      {data.returned_subtotal} PKR
                    </PixelText>
                  )}
                </PixelFlexBox>
              </PixelFlexBox>
              {data?.logistics_fulfillment_services && (
                <PixelText
                  textSize="20px"
                  style={{
                    fontWeight: '500',
                    marginTop: '20px',
                    marginLeft: '10px',
                  }}
                >
                  Services
                </PixelText>
              )}
              <PixelFlexBox padding="0 30px">
                <PixelFlexBox flexDirection="column">
                  {data?.logistics_fulfillment_services && (
                    <PixelText>Logistics & Fulfillment Services</PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 25px"
                  >
                    {data?.handling_fee && <PixelText>Handling Fee</PixelText>}
                  </PixelFlexBox>
                  {data?.advertising_marketing_services && (
                    <PixelText>Advertising & Marketing Services</PixelText>
                  )}
                  {data?.financial_services && (
                    <PixelText>Financial Services</PixelText>
                  )}
                  {data?.inventory_management && (
                    <PixelText>Inventory Management</PixelText>
                  )}
                  {data?.services_VAT && <PixelText>VAT</PixelText>}
                  {data?.services_subtotal && (
                    <PixelText
                      style={{
                        marginLeft: '500px',
                        marginTop: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      Subtotal
                    </PixelText>
                  )}
                </PixelFlexBox>
                <PixelFlexBox
                  style={{ textAlign: 'end' }}
                  flexDirection="column"
                >
                  {data?.logistics_fulfillment_services && (
                    <PixelText>
                      {data.logistics_fulfillment_services} PKR
                    </PixelText>
                  )}
                  <PixelFlexBox
                    style={{ color: 'gray' }}
                    flexDirection="column"
                    margin="0 0 10px 0"
                  >
                    {data?.handling_fee && (
                      <PixelText>{data.handling_fee} PKR</PixelText>
                    )}
                  </PixelFlexBox>
                  {data?.advertising_marketing_services && (
                    <PixelText>
                      {data.advertising_marketing_services} PKR
                    </PixelText>
                  )}
                  {data?.financial_services && (
                    <PixelText>{data.financial_services} PKR</PixelText>
                  )}
                  {data?.inventory_management && (
                    <PixelText>{data.inventory_management} PKR</PixelText>
                  )}
                  {data?.services_VAT && (
                    <PixelText>{data.services_VAT} PKR</PixelText>
                  )}
                  {data?.services_subtotal && (
                    <PixelText
                      style={{ marginTop: '30px', fontWeight: 'bold' }}
                    >
                      {data?.services_subtotal} PKR
                    </PixelText>
                  )}
                </PixelFlexBox>
              </PixelFlexBox>
            </SectionContent>
          ) : (
            <SectionContent>
              <PixelText style={{ textAlign: 'center' }} textSize="25px">
                Details not found
              </PixelText>
            </SectionContent>
          )}
        </Section>
      </PixelPageContainer>
    </>
  )
}

export default DarazStatmentDetails
